import { Box, Text } from '@gazin/ecommerce-ui'
import { useEffect, useMemo, useRef, useState } from 'react'
import { parseColor, pickReadableColor } from 'src/util/colorContrast'
import { formatHtmlContent } from 'src/util/formatHtmlContent'
import { sanitizeSeoHtml } from 'src/util/sanitizeSeoHtml'

const LEADING_HEADING = /^\s*<h[1-6][^>]*>(.*?)<\/h[1-6]>/is

const COLLAPSED_HEIGHT = '7.875rem'

// Mesmo tom das demais seções em tema Black Friday (setorServicos/styles.ts:13).
export const DARK_BACKGROUND = '#141723'

// Cores já configuradas no hotsite (Cor Inicial, Cor Final e Cor Padrão para Texto).
export type SeoContentColors = {
  start?: string | null
  end?: string | null
  text?: string | null
}

type SeoContentProps = {
  content?: string | null
  darkTheme?: boolean
  title?: string | null
  colors?: SeoContentColors
  // 'wide': conteúdo SEO dos hotsites, com tabela, imagem e vídeo (TNDC-436, P2).
  allowlist?: 'default' | 'wide'
}

type ResolvedColors = {
  background: string
  readableOn: (color?: string | null) => string
}

// Personalização = Cor Inicial válida, a mesma regra do header e da newsletter do
// hotsite. Sem ela, null: o bloco segue o visual padrão (TNDC-404). TNDC-436, P6.
const resolveColors = (colors: SeoContentColors): ResolvedColors | null => {
  if (!parseColor(colors.start)) return null

  const start = colors.start as string
  const end = parseColor(colors.end) ? (colors.end as string) : null
  const backgrounds = [start, end]

  return {
    background:
      end && end.toLowerCase() !== start.toLowerCase()
        ? `linear-gradient(85.36deg, ${start} 3.91%, ${end} 98.18%)`
        : start,
    // Contraste mínimo contra as duas pontas do degradê, avaliado por elemento.
    readableOn: (color) => pickReadableColor(color, backgrounds),
  }
}

const stripTags = (html: string) => html.replace(/<[^>]*>/g, '')

const hasText = (html: string) => stripTags(html).trim() !== ''

const TAG_TOKEN = /<(\/?)([a-z0-9]+)[^>]*>/gi
const INLINE_TAGS = new Set([
  'a',
  'strong',
  'b',
  'em',
  'i',
  'u',
  'small',
  'span',
])

// Opera sobre a saída do sanitizeSeoHtml, onde todo `<` restante abre uma tag
// permitida e nenhum atributo contém `<` ou `>`.
const isBalancedInlineHtml = (html: string) => {
  const open: string[] = []

  for (const [, closing, name] of Array.from(html.matchAll(TAG_TOKEN))) {
    const tag = name.toLowerCase()
    if (tag === 'br') continue
    if (!INLINE_TAGS.has(tag)) return false
    if (!closing) open.push(tag)
    else if (open.pop() !== tag) return false
  }

  return open.length === 0
}

export const hasSeoContent = (html?: string | null) =>
  hasText(sanitizeSeoHtml(html))

export const SeoContent = ({
  content,
  darkTheme = false,
  title,
  colors,
  allowlist = 'default',
}: SeoContentProps) => {
  const [expanded, setExpanded] = useState(false)
  // Nasce true para o botão existir no HTML do servidor: a medição só é possível
  // depois de montar, e o conteúdo não pode depender dela. TNDC-404, review !584.
  const [overflows, setOverflows] = useState(true)
  const clampRef = useRef<HTMLDivElement>(null)

  // Ponto único de sanitização: o conteúdo vem do CMS e passou a ser renderizado no
  // servidor, onde <script> executa — no cliente entraria inerte. TNDC-404, Vigilant.
  const { embeddedTitle, formattedBody } = useMemo(() => {
    const safe = sanitizeSeoHtml(content, { allowlist })
    const [, headingHtml, bodyWithoutTitle] = safe.split(LEADING_HEADING)

    const titleHtml = headingHtml?.trim()

    return {
      embeddedTitle:
        titleHtml && hasText(titleHtml)
          ? isBalancedInlineHtml(titleHtml)
            ? titleHtml
            : stripTags(titleHtml)
          : undefined,
      formattedBody: formatHtmlContent(bodyWithoutTitle ?? safe),
    }
  }, [content, allowlist])

  const { start, end, text } = colors ?? {}
  const custom = useMemo(
    () => resolveColors({ start, end, text }),
    [start, end, text]
  )
  const titleColor = custom ? custom.readableOn(text) : 'primary.500'
  const bodyColor = custom
    ? custom.readableOn(text)
    : darkTheme
    ? 'neutral.100'
    : 'neutral.600'
  const linkColor = custom ? custom.readableOn(text) : 'primary.500'
  const actionColor = custom ? custom.readableOn(text) : 'primary.500'
  const background = darkTheme ? DARK_BACKGROUND : 'neutral.0'

  // Título do conteúdo vai como HTML sanitizado (entidades e links); desbalanceado cai
  // para texto, ou vazaria tags pela página. O `title` da prop é texto puro. TNDC-427.
  const titleContent = title
    ? { children: title }
    : embeddedTitle && { dangerouslySetInnerHTML: { __html: embeddedTitle } }

  useEffect(() => {
    // Mede o CONTÊINER do recorte, não o corpo: é nele que a max-height vive, e no
    // corpo scrollHeight e clientHeight são sempre iguais.
    const clamp = clampRef.current
    // Sem layout (clientHeight 0) não há o que concluir: mantém a ação visível em vez
    // de escondê-la por medição inexistente.
    if (!clamp?.clientHeight) return

    setOverflows(clamp.scrollHeight > clamp.clientHeight)
  }, [formattedBody])

  const hasBody = hasText(formattedBody)

  if (!hasBody && !titleContent) return null

  return (
    <Box
      width="100%"
      maxWidth="81.875rem"
      marginX="auto"
      padding="1.5rem 2rem"
      {...(custom
        ? { background: custom.background }
        : { backgroundColor: background })}
      border="1px solid"
      borderColor={
        custom ? 'transparent' : darkTheme ? 'whiteAlpha.300' : 'neutral.75'
      }
      borderRadius="0.5rem"
      data-testid="seo-content"
    >
      <Box display="flex" flexDirection="column" gap="1.5rem">
        {!!titleContent && (
          <Text
            as="h2"
            margin="0"
            color={titleColor}
            fontSize="1.25rem"
            fontWeight="600"
            lineHeight="1.625rem"
            sx={{ a: { color: 'inherit', textDecoration: 'underline' } }}
            {...titleContent}
          />
        )}

        {hasBody && (
          <Box
            ref={clampRef}
            position="relative"
            overflow="hidden"
            maxHeight={expanded || !overflows ? 'none' : COLLAPSED_HEIGHT}
            color={bodyColor}
            fontSize="1rem"
            fontWeight="400"
            lineHeight="1.5rem"
            sx={{
              p: { margin: 0 },
              a: { color: linkColor, textDecoration: 'underline' },
              'ul, ol': { paddingLeft: '1.25rem' },
              'ul li': { listStyle: 'disc' },
              // Só a allowlist ampla (hotsite) produz tabela, imagem e vídeo.
              table: { width: '100%', borderCollapse: 'collapse' },
              'th, td': {
                border: '1px solid',
                borderColor: 'currentColor',
                padding: '0.25rem 0.5rem',
              },
              'img, iframe': { maxWidth: '100%' },
              img: { height: 'auto' },
              // Sobre degradê não existe cor sólida para o véu: o recorte esmaece o
              // próprio texto.
              ...(custom &&
                !expanded &&
                overflows && {
                  maskImage:
                    'linear-gradient(to bottom, black calc(100% - 4rem), transparent)',
                }),
            }}
          >
            <Box
              data-testid="seo-content-body"
              dangerouslySetInnerHTML={{ __html: formattedBody }}
            />
            {!custom && !expanded && overflows && (
              <Box
                data-testid="seo-content-fade"
                position="absolute"
                right="0"
                bottom="0"
                left="0"
                height="4rem"
                pointerEvents="none"
                bgGradient={`linear(to-b, transparent 0%, ${background} 71.15%)`}
              />
            )}
          </Box>
        )}

        {/* minHeight: o line-height de 19.2px do Figma é alvo de toque pequeno demais no
            mobile (WCAG 2.5.5). O desktop segue fiel à spec. TNDC-404, Lens C9. */}
        {hasBody && overflows && (
          <Box
            as="button"
            type="button"
            width="fit-content"
            minHeight={{ base: '2.75rem', lg: 'auto' }}
            display="flex"
            alignItems="center"
            padding="0"
            border="0"
            background="none"
            color={actionColor}
            cursor="pointer"
            fontSize="1rem"
            fontWeight="600"
            lineHeight="1.2rem"
            aria-expanded={expanded}
            data-testid="seo-content-action"
            onClick={() => setExpanded((current) => !current)}
          >
            {expanded ? 'Mostrar menos detalhes' : 'Mostrar mais detalhes'}
          </Box>
        )}
      </Box>
    </Box>
  )
}
